import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { acquireModalScrollLock, ROUTE_DISMISS_MODALS_EVENT } from '../../utils/modalUtils';
import { usePresenceTransition } from '../../hooks/usePresenceTransition';

/**
 * Mount modal markup on document.body so position:fixed is viewport-relative
 * (not trapped by .main-content overflow / header backdrop-filter).
 * Also acquires the shared scroll lock for the app shell.
 *
 * Optional `open` enables delayed unmount for exit animations. Prefer
 * usePresenceTransition in the parent when the modal tree is large.
 * Pass `onRouteDismiss` so keep-alive portals close on shell navigation.
 */
export default function AdsModalPortal({
  open = true,
  onRouteDismiss,
  children,
  lockScroll = true
}) {
  const { present } = usePresenceTransition(Boolean(open), { exitMs: 160 });

  useEffect(() => {
    if (!present || !lockScroll) return undefined;
    return acquireModalScrollLock();
  }, [present, lockScroll]);

  useEffect(() => {
    if (typeof onRouteDismiss !== 'function') return undefined;
    const onRoute = () => {
      if (open) onRouteDismiss();
    };
    window.addEventListener(ROUTE_DISMISS_MODALS_EVENT, onRoute);
    return () => window.removeEventListener(ROUTE_DISMISS_MODALS_EVENT, onRoute);
  }, [open, onRouteDismiss]);

  if (!present || typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}
